<template>
  <ProductCard
    v-if="product"
    :key="product.id"
    :product="viewModel"
    :currency="currency"
    :quantity="submittableCartItemQuantity"
    :cart-quantity="cartQuantity"
    :selections="selections"
    :sold-out="soldOut"
    :unavailable="Boolean(unavailable)"
    :highlighted="isHighlighted"
    :line-price-cents="linePriceCents"
    :line-original-price-cents="lineOriginalPriceCents"
    :base-price-original-cents="basePriceOriginalCents"
    :product-id="product.id"
    test-id-prefix="product-card"
    @open="openModal"
    @remove="remove"
  >
    <template #below>
      <ProductDaySplit :product="product" :submittable-cart-item="submittableCartItem" />
    </template>
  </ProductCard>
</template>

<script>
import ProductCard from '@/components/Product/ProductCard.vue'
import ProductDaySplit from '@/components/Product/ProductDaySplit'
import { computed, inject, ref } from 'vue'
import { useStore } from 'vuex'
import {
  buildV1ProductViewModel,
  useProductModal,
  v1BasePrice,
  v1DefaultPrice,
  v1IsUpsell
} from './ProductModalHost.vue'
import { blankSubmittableCartItem } from '@/utils/submittableCartItem'
import { cartItemVariantOptionsToSelection, lineUnitPriceCents, selectionSummary } from '@/models/productViewModel'

export default {
  name: 'Product',

  components: { ProductCard, ProductDaySplit },

  props: {
    product: {
      required: true
    },
    submittableCartItem: {
      required: false
    },
    categoryProductStorePair: {
      required: false
    },
    inCart: {
      required: false
    },
    cartQuantity: {
      type: Number,
      default: null
    },
    // Set by NoLongerAvailableCategory. The product is off the menu for the chosen fulfillment, so
    // the card drops its modal and offers removal instead.
    unavailable: {
      required: false
    }
  },

  setup(props) {
    const store = useStore()
    const product = computed(() => store.state.objects.product?.[props.product?.id])
    const { openProductModal } = useProductModal()
    // ProductContainer used to own this; the shared card takes `highlighted` as a plain prop so it
    // stays free of either version's injection keys.
    const v1HighlightedProductId = inject('v1HighlightedProductId', ref(null))

    return { product, openProductModal, v1HighlightedProductId }
  },

  computed: {
    submittableCartItemQuantity() {
      return this.submittableCartItem?.quantity || 0
    },
    isUpsell() {
      return v1IsUpsell(this.$store, this.categoryProductStorePair)
    },
    quantityRemaining() {
      return this.categoryProductStorePair?.attributes?.quantity_remaining
    },
    soldOut() {
      return this.submittableCartItemQuantity == 0 && this.quantityRemaining != null && this.quantityRemaining <= 0
    },
    basePrice() {
      return v1BasePrice(this.$store, {
        product: this.product,
        categoryProductStorePair: this.categoryProductStorePair,
        isUpsell: this.isUpsell
      })
    },
    viewModel() {
      return buildV1ProductViewModel(this.$store, {
        product: this.product,
        categoryProductStorePair: this.categoryProductStorePair,
        cartItem: this.submittableCartItem
      })
    },
    currency() {
      return this.basePrice?.currency_iso || 'USD'
    },

    lineSelection() {
      return cartItemVariantOptionsToSelection(this.submittableCartItem?.cart_item_variant_options_attributes)
    },

    selections() {
      return selectionSummary(this.viewModel, this.lineSelection)
    },

    linePriceCents() {
      return lineUnitPriceCents({
        unitCents: this.submittableCartItem?.net_price_plus_net_variants_price?.cents ?? null,
        viewModel: this.viewModel,
        selection: this.lineSelection,
        quantity: this.submittableCartItemQuantity
      })
    },

    // No view-model fallback: an undiscounted line has nothing to strike, and computing one from
    // the catalog price would put a "was" on every line whose base price merely moved.
    lineOriginalPriceCents() {
      return lineUnitPriceCents({
        unitCents: this.submittableCartItem?.default_price_plus_variants_price?.cents ?? null,
        quantity: this.submittableCartItemQuantity
      })
    },

    basePriceOriginalCents() {
      const price = v1DefaultPrice(this.$store, {
        product: this.product,
        categoryProductStorePair: this.categoryProductStorePair,
        isUpsell: this.isUpsell
      })
      return price?.cents ?? null
    },

    isHighlighted() {
      return this.product?.id != null && String(this.v1HighlightedProductId) === String(this.product.id)
    },

    // In the cart view a card *is* one cart line (CategoryProductStorePairContainer renders one
    // per submittable item), so tapping it edits that line. In the full product list the card
    // stands for the product, and a tap starts blank: ordering the same product twice with
    // different modifiers is the common case on a menu, and handing over the existing line there
    // would turn a second variation into an edit of the first.
    editsExistingLine() {
      return Boolean(this.inCart) && this.submittableCartItemQuantity > 0
    },

    modalSubmittableCartItem() {
      if (this.editsExistingLine) return this.submittableCartItem
      return blankSubmittableCartItem({
        categoryProductStorePairId: this.categoryProductStorePair?.id,
        fulfillmentGroupDate: this.submittableCartItem?.fulfillment_group_date || null,
        isUpsell: this.isUpsell
      })
    }
  },

  methods: {
    openModal() {
      if (this.soldOut) return
      this.openProductModal({
        productId: this.product.id,
        categoryProductStorePairId: this.categoryProductStorePair?.id,
        submittableCartItem: this.modalSubmittableCartItem
      })
    },

    // A quantity of 0 is how V1 has always removed a line - the store action filters it out of the
    // submittable cart and re-posts (store/index.js:1543).
    remove() {
      if (!this.submittableCartItem?.frontend_key) return
      this.$store.dispatch('updateSubmittableCart', {
        submittableCartItem: { ...this.submittableCartItem, quantity: 0 }
      })
    }
  }
}
</script>
